/* ──────────────────────────────────────────────
 *  Mesure du parcours (spec Parcours § 13) : les
 *  faits du tunnel, relayés à l'hôte par
 *  `onFunnelEvent`. Chaque fait part quand il est
 *  acquis (étape affichée, créneau validé,
 *  pré-réservation créée), jamais sur une intention.
 * ────────────────────────────────────────────── */

import { useCallback, useEffect, useRef } from 'react';
import { minorUnitToSiteEventValue, resolveSiteEventCurrency, type SiteEventItem } from '@cfixe-monorepo/shared';
import type { BookingContext, BookingFunnelEvent, BookingStep } from '../types';
import { visibleSteps, type VisibleBookingStep } from './booking-machine';

const MS_PER_HOUR = 3_600_000;

/** Une remise (code promo) s'applique : lignes de remise du bloc `amounts` en cours. */
function hasPromotion(context: BookingContext): boolean {
  const amounts = context.preReservationData?.amounts ?? context.cartSummary?.amounts;
  return (amounts?.discount_lines?.length ?? 0) > 0;
}

export interface BookingFunnel {
  /** Pré-réservation expirée (compte à rebours) : le client revient aux créneaux. */
  reportExpired: () => void;
}

/* ── Paiement hébergé (Klarna…) : la page se décharge pour rediriger, pas pour abandonner ──
   `confirmPayment` quitte la page vers le prestataire ; `booking_abandoned` partirait alors AVANT
   `booking_confirmed`, contre le contrat publié (un abandon est « avant la confirmation »). L'étape
   de paiement pose l'indicateur juste avant la confirmation Stripe et le lève dès qu'elle revient
   sans rediriger (refus, erreur, carte réglée en ligne). Un retour de redirection charge un nouveau
   document, qui repart indicateur levé ; une page restaurée depuis le cache arrière (le client
   revient du prestataire sans payer) le lève aussi, pour que son départ suivant compte. Un seul
   tunnel par page : l'indicateur vit au module. */
let paymentRedirectPending = false;

/** Étape de paiement : `true` juste avant la confirmation Stripe, `false` dès qu'elle revient sans rediriger. */
export function setPaymentRedirectPending(pending: boolean): void {
  paymentRedirectPending = pending;
}

export function useBookingFunnel(args: {
  currentStep: BookingStep | 'loading' | 'error';
  context: BookingContext;
  onFunnelEvent?: (event: BookingFunnelEvent) => void;
  /** Retour de paiement en cours de vérification : le client ne voit pas les étapes, rien ne part. */
  muted: boolean;
  /** Devise du pro (`begin_checkout`). */
  currency: string;
}): BookingFunnel {
  const { currentStep, context, onFunnelEvent, muted, currency } = args;
  const onEventRef = useRef(onFunnelEvent);
  useEffect((): void => {
    onEventRef.current = onFunnelEvent;
  }, [onFunnelEvent]);
  const emit = useCallback((event: BookingFunnelEvent): void => onEventRef.current?.(event), []);

  const startedRef = useRef(false);
  /* Parcours terminé (confirmé ou page quittée) : plus d'abandon à signaler. */
  const finishedRef = useRef(false);
  const lastStepRef = useRef<VisibleBookingStep | 'loading'>('loading');
  const prevStepRef = useRef<BookingStep | 'loading' | 'error'>('loading');
  const preReservationRef = useRef<string | undefined>(undefined);
  const checkoutStartedRef = useRef(false);

  useEffect((): void => {
    const previous = prevStepRef.current;
    if (previous === currentStep) return;
    prevStepRef.current = currentStep;
    if (currentStep === 'loading') return;
    if (currentStep === 'success') {
      finishedRef.current = true;
      return;
    }
    if (currentStep === 'error') {
      emit({ name: 'booking_abandoned', params: { last_step: lastStepRef.current, reason: 'error' } });
      return;
    }
    // Demande de devis : hors du parcours de réservation, aucune étape mesurée.
    if (currentStep === 'quote') return;
    lastStepRef.current = currentStep;
    if (muted) return;

    if (!startedRef.current) {
      startedRef.current = true;
      emit({ name: 'booking_started', params: { prestation_count: context.cartItems.length } });
    }
    const steps = visibleSteps(context);
    emit({
      name: 'booking_step',
      params: {
        step: currentStep,
        step_index: steps.indexOf(currentStep) + 1,
        step_total: steps.length,
        entry_mode: context.entryMode,
      },
    });
    if (previous === 'slot' && currentStep === 'checkout' && context.selectedSlot) {
      const leadTimeMs = Date.parse(context.selectedSlot.slot_start) - Date.now();
      emit({
        name: 'booking_slot_selected',
        params: {
          lead_time_hours: Math.max(0, Math.round(leadTimeMs / MS_PER_HOUR)),
          promotion: hasPromotion(context),
        },
      });
    }
  }, [currentStep, context, muted, emit]);

  /* `begin_checkout` : la première étape affichée avec une sélection, une fois son total chiffré par le
     serveur (le résumé est effacé à chaque changement du panier, donc toujours celui de la sélection). */
  useEffect((): void => {
    if (checkoutStartedRef.current || muted) return;
    if (currentStep === 'loading' || currentStep === 'error' || currentStep === 'success') return;
    const amounts = context.cartSummary?.amounts;
    if (context.cartItems.length === 0 || !amounts) return;
    checkoutStartedRef.current = true;
    emit({
      name: 'begin_checkout',
      params: {
        value: minorUnitToSiteEventValue(amounts.net_total_raw),
        currency: resolveSiteEventCurrency(currency),
        items: context.cartItems.map(
          (item): SiteEventItem => ({
            item_id: String(item.prestation.prestation_id),
            item_name: item.prestation.name,
            price: minorUnitToSiteEventValue(item.selectedPrice.price),
            quantity: item.quantity * (item.party_size ?? 1),
          }),
        ),
        promotion: hasPromotion(context),
        entry_mode: context.entryMode,
      },
    });
  }, [currentStep, context, muted, emit, currency]);

  useEffect((): void => {
    const uuid = context.preReservationUuid;
    if (!uuid || uuid === preReservationRef.current) return;
    preReservationRef.current = uuid;
    emit({ name: 'booking_prereserved', params: { promotion: hasPromotion(context) } });
  }, [context, emit]);

  useEffect((): (() => void) => {
    const onPageHide = (): void => {
      if (!startedRef.current || finishedRef.current || paymentRedirectPending) return;
      finishedRef.current = true;
      emit({ name: 'booking_abandoned', params: { last_step: lastStepRef.current, reason: 'closed' } });
    };
    const onPageShow = (event: Event): void => {
      if ((event as PageTransitionEvent).persisted) paymentRedirectPending = false;
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    return (): void => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [emit]);

  const reportExpired = useCallback(
    (): void => emit({ name: 'booking_abandoned', params: { last_step: 'checkout', reason: 'expired' } }),
    [emit],
  );
  return { reportExpired };
}
